import { useGlobalSearchSelection } from '../../cmsSearchBridge';
import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import HenrriDevisModal from "./HenrriDevisModal";
import ClientAutocomplete from "./ClientAutocomplete";
import DevisValidesModal from "./DevisValidesModal";
import "./SuiviProduction.css";

const CATEGORIES = [
    { value: "", label: "Toutes catégories" },
    { value: "informatique", label: "Informatique" },
    { value: "telephonie", label: "Téléphonie" },
    { value: "ged", label: "GED" },
    { value: "alarme", label: "Alarme" },
    { value: "developpement_web", label: "Développement web" },
];

const CATEGORIE_COLORS = {
    informatique: { bg: "#e3f0ff", color: "#1565c0", border: "#90caf9" },
    telephonie: { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
    ged: { bg: "#fff8e1", color: "#f57f17", border: "#ffe082" },
    alarme: { bg: "#fce4ec", color: "#c62828", border: "#ef9a9a" },
    developpement_web: { bg: "#f3e8ff", color: "#6b21a8", border: "#d8b4fe" },
};

const SuiviProduction = () => {
    const navigate = useNavigate();

    const currentUser = { name: "Admin", role: "Admin" };
    const ALLOWED_BILLING_USERS = ["Admin", "Comptable"];
    const canAccessBilling = ALLOWED_BILLING_USERS.includes(currentUser.name);
    const nextYear = new Date().getFullYear() + 1;

    const etapesList = [
        { id: "1", title: "En attente d'affectation", category: "common" },
        { id: "2", title: "Commande pièce/services", category: "common" },
        { id: "101", title: "Récéption piéces", category: "alarme" },
        { id: "102", title: "Planification chantier", category: "alarme" },
        { id: "103", title: "Production chantier", category: "alarme" },
        { id: "201", title: "Commande Portabilité", category: "telecom" },
        { id: "202", title: "Commande Lien", category: "telecom" },
        { id: "203", title: "Planification chantier", category: "telecom" },
        { id: "204", title: "Production chantier", category: "telecom" },
        { id: "300", title: "Chantier en cours", category: "common" },
        { id: "400", title: "Facturation", category: "common" },
    ];

    const [columns, setColumns] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [globalDocument, setGlobalDocument] = useState(null);
    useGlobalSearchSelection(selection => {
        if (selection.documentId) setGlobalDocument(selection);
        else { setSearchTerm(selection.query); setFilterCategorie(''); }
    });
    const [filterCategorie, setFilterCategorie] = useState("");
    const [activeTunnel, setActiveTunnel] = useState("alarme");
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [closingModal, setClosingModal] = useState(null);
    const [responsables, setResponsables] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    const [expandedNoteId, setExpandedNoteId] = useState(null);
    const [workboardSendingId, setWorkboardSendingId] = useState(null);

    // Recherche devis Henrri : "new" (modale création) ou "edit" (modale édition) ou null
    const [showDevisSearch, setShowDevisSearch] = useState(null);
    const [devisSearchTerm, setDevisSearchTerm] = useState("");

    // Devis validés à traiter
    const [showDevisValides, setShowDevisValides] = useState(false);
    const [devisValidesCount, setDevisValidesCount] = useState(0);

    const [showAnneeProModal, setShowAnneeProModal] = useState(false);
    const [anneeProItems, setAnneeProItems] = useState([]);
    const [loadingAnneePro, setLoadingAnneePro] = useState(false);

    const [newSuivi, setNewSuivi] = useState({
        client_nom: "",
        henrri_customer_id: null,
        objet_inter: "",
        id_etape: "1",
        id_responsable: "",
        is_urgent: 0,
        date_echeance: "",
        num_devis: "",
        montant_chantier: "",
        type_tunnel: "alarme",
        num_facture: "",
        notes: "",
        categorie: "informatique",
    });

    useEffect(() => {
        const receiveWorkboardResult = (event) => {
            if (event.data?.type !== "AMS_WORKBOARD_IMPORT_RESULT") return;
            setWorkboardSendingId(null);
            window.alert(event.data.message || (event.data.success ? "Tâche ajoutée au Workboard." : "Envoi impossible."));
        };
        window.addEventListener("message", receiveWorkboardResult);
        return () => window.removeEventListener("message", receiveWorkboardResult);
    }, []);

    const sendToWorkboard = (event, item) => {
        event.preventDefault();
        event.stopPropagation();
        if (window.parent === window) {
            window.alert("Ouvrez le Kanban depuis le CMS pour envoyer cette tâche au Workboard.");
            return;
        }
        setWorkboardSendingId(String(item.id));
        const stage = etapesList.find((entry) => String(entry.id) === String(item.id_etape));
        window.parent.postMessage({
            type: "AMS_WORKBOARD_IMPORT",
            requestId: `${item.id}-${Date.now()}`,
            task: { ...item, stage_title: stage?.title || `Étape ${item.id_etape}` },
        }, "*");
    };

    // --- API CALLS ---
    const fetchResponsables = async () => {
        try {
            const res = await fetch("https://kanban.amservices.eu/api/get_responsables.php");
            const data = await res.json();
            if (data.success) {
                setResponsables(data.data);
                if (data.data.length > 0)
                    setNewSuivi(prev => ({ ...prev, id_responsable: data.data[0].id }));
            }
        } catch (err) { console.error(err); }
    };

    const fetchSuivis = async () => {
        try {
            const res = await fetch("https://kanban.amservices.eu/api/get_suivis.php");
            const data = await res.json();
            if (data.success) {
                const newColumns = {};
                etapesList.forEach(et => { newColumns[et.id] = { ...et, items: [] }; });
                data.data.forEach(item => {
                    const stepId = String(item.id_etape);
                    if (newColumns[stepId]) newColumns[stepId].items.push(item);
                });
                setColumns(newColumns);
            }
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    const fetchAnneeProItems = async () => {
        setLoadingAnneePro(true);
        try {
            const res = await fetch("https://kanban.amservices.eu/api/get_suivis_annee_pro.php");
            const data = await res.json();
            if (data.success) setAnneeProItems(data.data);
        } catch (err) { console.error(err); } finally { setLoadingAnneePro(false); }
    };

    const handleOpenAnneePro = () => {
        setShowAnneeProModal(true);
        fetchAnneeProItems();
    };

    const handleRemettreMaintenant = async (id) => {
        try {
            await fetch("https://kanban.amservices.eu/api/update_annee_prevue.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id, annee_prevue: null }),
            });
            setAnneeProItems(prev => prev.filter(i => i.id !== id));
            fetchSuivis();
        } catch (err) { console.error(err); }
    };

    useEffect(() => {
        fetchResponsables();
        fetchSuivis();
        fetchAnneeProItems();
        fetchDevisValidesCount();
    }, []);

    // Sélection d'un devis Henrri depuis la popup de recherche
    const handleSelectDevis = (devis) => {
        const numero = devis.numero || "";
        if (showDevisSearch === "edit") {
            setEditingItem(prev => ({ ...prev, num_devis: numero }));
        } else {
            setNewSuivi(prev => ({ ...prev, num_devis: numero }));
        }
        setShowDevisSearch(null);
    };

    // Compteur de devis validés à traiter (pour le badge de la case)
    const fetchDevisValidesCount = async () => {
        try {
            const res = await fetch("https://kanban.amservices.eu/api/get_devis_valides_a_traiter.php");
            const data = await res.json();
            if (data.success) setDevisValidesCount(data.data.length);
        } catch (err) { console.error(err); }
    };

    // Import d'un devis validé : ouvre la modale de création pré-remplie
    const handleImportDevisValide = (devis) => {
        setNewSuivi(prev => ({
            ...prev,
            client_nom: devis.client || "",
            henrri_customer_id: devis.henrri_customer_id || null,
            num_devis: devis.numero || "",
            montant_chantier: devis.montant_ht != null ? String(devis.montant_ht) : "",
            objet_inter: devis.titre || "",
        }));
        setShowDevisValides(false);
        setShowModal(true);
        fetchDevisValidesCount();
    };

    const toggleNoteView = (e, itemId) => {
        e.stopPropagation();
        setExpandedNoteId(expandedNoteId === itemId ? null : itemId);
    };

    const sortItems = (items) =>
        [...items].sort((a, b) => {
            if (b.is_urgent !== a.is_urgent) return b.is_urgent - a.is_urgent;
            return new Date(a.date_echeance || "9999-12-31") - new Date(b.date_echeance || "9999-12-31");
        });

    const handleCloseModal = (modalName, setModalState) => {
        setClosingModal(modalName);
        setTimeout(() => { setModalState(false); setClosingModal(null); }, 280);
    };

    const handleAddSuivi = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch("https://kanban.amservices.eu/api/add_suivi.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newSuivi),
            });
            if ((await res.json()).success) {
                handleCloseModal("new", setShowModal);
                setNewSuivi({ ...newSuivi, client_nom: "", henrri_customer_id: null, objet_inter: "", montant_chantier: "", num_devis: "", notes: "" });
                fetchSuivis();
                fetchDevisValidesCount();
            }
        } catch (err) { console.error(err); }
    };

    const handleUpdateSuivi = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch("https://kanban.amservices.eu/api/update_full_suivi.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(editingItem),
            });
            if ((await res.json()).success) {
                handleCloseModal("edit", setShowEditModal);
                fetchSuivis();
            }
        } catch (err) { console.error(err); }
    };

    const handleDeleteSuivi = async (id) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce dossier ? Cette action est irréversible.")) return;
        try {
            const res = await fetch("https://kanban.amservices.eu/api/delete_suivi.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id }),
            });
            const data = await res.json();
            if (data.success) {
                handleCloseModal("edit", setShowEditModal);
                fetchSuivis();
            } else { alert("Erreur lors de la suppression côté serveur."); }
        } catch (err) { console.error(err); alert("Une erreur de communication est survenue."); }
    };

    const onDragEnd = async (result) => {
        const { source, destination } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;
        const itemToMove = columns[source.droppableId].items[source.index];
        const newCols = { ...columns };
        newCols[source.droppableId].items.splice(source.index, 1);

        const isFacturation = destination.droppableId === "400";
        const isAnneePro = destination.droppableId === "ANNEEPRO";

        if (!isFacturation && !isAnneePro) {
            newCols[destination.droppableId].items.push({ ...itemToMove, id_etape: destination.droppableId });
            newCols[destination.droppableId].items = sortItems(newCols[destination.droppableId].items);
        }
        setColumns(newCols);

        try {
            if (isAnneePro) {
                await fetch("https://kanban.amservices.eu/api/update_annee_prevue.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ id: itemToMove.id, annee_prevue: nextYear }),
                });
                fetchAnneeProItems();
            } else {
                await fetch("https://kanban.amservices.eu/api/update_etape.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ id: itemToMove.id, id_etape: destination.droppableId }),
                });
                if (isFacturation) navigate("/facturation");
            }
        } catch (err) { fetchSuivis(); }
    };

    const getFilteredItems = (items) => {
        let result = items;
        if (searchTerm) {
            const low = searchTerm.toLowerCase();
            result = result.filter(i =>
                i.client_nom.toLowerCase().includes(low) ||
                (i.num_devis && i.num_devis.toLowerCase().includes(low))
            );
        }
        if (filterCategorie) {
            result = result.filter(i => i.categorie === filterCategorie);
        }
        return result;
    };

    const totalCA = Object.values(columns).reduce((acc, col) =>
        acc + col.items.reduce((sum, item) => sum + parseFloat(item.montant_chantier || 0), 0), 0
    );

    // Totaux par catégorie
    const totauxParCategorie = CATEGORIES.filter(({ value }) => value).map(({ value: cat }) => ({
        cat,
        total: Object.values(columns).reduce((acc, col) =>
            acc + col.items.filter(i => i.categorie === cat).reduce((sum, item) => sum + parseFloat(item.montant_chantier || 0), 0), 0
        )
    }));

    const totalAnneePro = anneeProItems.reduce((sum, item) => sum + parseFloat(item.montant_chantier || 0), 0);

    // Badge catégorie
    const CategorieBadge = ({ categorie }) => {
        if (!categorie) return null;
        const label = CATEGORIES.find(c => c.value === categorie)?.label || categorie;
        const style = CATEGORIE_COLORS[categorie] || { bg: "#eee", color: "#333", border: "#ccc" };
        return (
            <span className={`categorie-badge cat-${categorie}`} style={{
                display: "inline-block",
                fontSize: "0.7rem",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "20px",
                background: style.bg,
                color: style.color,
                border: `1px solid ${style.border}`,
                marginBottom: "6px",
                letterSpacing: "0.3px",
                textTransform: "uppercase",
            }}>
                {label}
            </span>
        );
    };

    const renderCard = (item, index) => (
        <Draggable key={item.id.toString()} draggableId={item.id.toString()} index={index}>
            {(provided, snapshot) => {
                const card = (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`kanban-card ${item.is_urgent == 1 ? "urgent" : ""} ${snapshot.isDragging ? "is-dragging" : ""} type-${item.type_tunnel} ${item.categorie ? `cat-${item.categorie}` : ""}`}
                        style={{ ...provided.draggableProps.style, "--card-index": Math.min(index, 8) }}
                        onClick={() => { setEditingItem(item); setShowEditModal(true); }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                event.preventDefault();
                                setEditingItem(item);
                                setShowEditModal(true);
                                return;
                            }
                            provided.dragHandleProps?.onKeyDown?.(event);
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Modifier le dossier ${item.client_nom}`}
                        title="Cliquer pour modifier ce dossier"
                    >
                        <div className="card-header">
                            <strong>
                                {item.client_nom}
                                {item.notes && (
                                    <span
                                        onClick={(e) => toggleNoteView(e, item.id)}
                                        className={`note-icon ${expandedNoteId === item.id ? "active" : ""}`}
                                        title="Voir/Masquer la note"
                                    >📝</span>
                                )}
                            </strong>
                        </div>

                        <div className="card-body">
                            <CategorieBadge categorie={item.categorie} />
                            <p className="objet-text" style={{ marginBottom: item.num_devis ? "4px" : "12px" }}>
                                🛠 {item.objet_inter || "Pas d'objet"}
                            </p>
                            {item.num_devis && (
                                <p className="devis-text" style={{ fontSize: "0.8rem", color: "#888", margin: "0 0 10px 0", fontStyle: "italic" }}>
                                    📄 Devis : <strong>{item.num_devis}</strong>
                                </p>
                            )}
                            {expandedNoteId === item.id && item.notes && (
                                <div className="card-note-content">
                                    <strong>Note interne :</strong>
                                    <p>{item.notes}</p>
                                </div>
                            )}
                            <div className="card-meta">
                                <span className="date-info">
                                    📅 {new Date(item.date_echeance).toLocaleDateString("fr-FR")}
                                </span>
                                <span className="price-tag">
                                    {parseFloat(item.montant_chantier || 0).toFixed(2)}€
                                </span>
                            </div>
                        </div>
                    </div>
                );
                return snapshot.isDragging ? createPortal(card, document.body) : card;
            }}
        </Draggable>
    );

    if (loading) return <div className="admin-page">Chargement @m Services...</div>;

    return (
        <div className="admin-page">
            <div className="header-kanban">
                <div className="header-top">
                    <div className="page-heading">
                        <span className="page-eyebrow">Pilotage des dossiers</span>
                        <h1>Suivi de production</h1>
                        <p>Une vue claire de chaque chantier, de l'affectation à la livraison.</p>
                    </div>
                    <div className="ca-badges-container">
                        <div className="ca-badge ca-badge-total">
                            💰 Total : {totalCA.toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €
                        </div>
                        {totauxParCategorie.map(({ cat, total }) => {
                            const style = CATEGORIE_COLORS[cat];
                            const label = CATEGORIES.find(c => c.value === cat)?.label;
                            return (
                                <div key={cat} className={`ca-badge-cat cat-${cat}`} style={{
                                    background: style.bg,
                                    color: style.color,
                                    border: `1px solid ${style.border}`,
                                }}>
                                    {label} : {total.toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="kanban-controls">

                    <div className="kanban-filter-row">
                        <div className="search-bar-container">
                            <span className="search-icon" aria-hidden="true">⌕</span>
                            <input
                                type="text"
                                placeholder="Recherche client..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <select
                            className="category-filter"
                            value={filterCategorie}
                            onChange={(e) => setFilterCategorie(e.target.value)}
                        >
                            {CATEGORIES.map(c => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="kanban-actions-right">
                        {!searchTerm && (
                            <button
                                type="button"
                                className="btn-devis-finalises"
                                onClick={() => setShowDevisValides(true)}
                            >
                                <span>Devis finalisés</span>
                                <strong>{devisValidesCount} à traiter</strong>
                            </button>
                        )}
                        <button className="btn-add" onClick={() => setShowModal(true)}>+ Nouveau Dossier</button>
                    </div>
                </div>
            </div>

            <div className="kanban-board">
                <DragDropContext onDragEnd={onDragEnd}>
                    {etapesList
                        .filter(et => (et.category === "common" || et.category === activeTunnel) && et.id !== "400")
                        .map((et, columnIndex) => {
                            const col = columns[et.id];
                            if (!col) return null;
                            const filtered = getFilteredItems(col.items);
                            return (
                                <div className="kanban-column" key={et.id} style={{ "--column-index": columnIndex }}>
                                    <h2>{et.title} <span className="count">{filtered.length}</span></h2>
                                    <Droppable droppableId={et.id}>
                                        {(provided) => (
                                            <div {...provided.droppableProps} ref={provided.innerRef} className="kanban-column-content">
                                                {filtered.map((item, idx) => renderCard(item, idx))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            );
                        })}

                    {!searchTerm && canAccessBilling && (
                        <Droppable droppableId="400">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`billing-trigger-zone ${snapshot.isDraggingOver ? "active-drop" : ""}`}
                                    onClick={() => navigate("/facturation")}
                                >
                                    <div className="billing-icon-large">💰</div>
                                    <span>FACTURATION</span>
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    )}

                    {!searchTerm && (
                        <Droppable droppableId="ANNEEPRO">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`anneepro-trigger-zone ${snapshot.isDraggingOver ? "active-drop" : ""}`}
                                    onClick={handleOpenAnneePro}
                                >
                                    <div className="anneepro-icon-large">📅</div>
                                    <span>ANNÉE {nextYear}</span>
                                    <span className="anneepro-total">
                                        {totalAnneePro.toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €
                                    </span>
                                    <span className="anneepro-count">
                                        {anneeProItems.length} dossier{anneeProItems.length > 1 ? "s" : ""}
                                    </span>
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    )}
                </DragDropContext>
            </div>

            {/* MODALE CRÉATION */}
            {showModal && (
                <div className={`modal-overlay ${closingModal === "new" ? "closing" : ""}`}>
                    <div className="modal-content">
                        <h2>Nouveau Dossier Am Services</h2>
                        <form onSubmit={handleAddSuivi}>

                            <div className="form-group">
                                <label>Catégorie</label>
                                <select value={newSuivi.categorie} onChange={(e) => setNewSuivi({ ...newSuivi, categorie: e.target.value })}>
                                    {CATEGORIES.filter(({ value }) => value).map(({ value, label }) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Client</label>
                                <ClientAutocomplete
                                    value={newSuivi.client_nom}
                                    required
                                    onChange={(nom) => setNewSuivi({ ...newSuivi, client_nom: nom })}
                                    onSelect={(client) => setNewSuivi({ ...newSuivi, client_nom: client.nom, henrri_customer_id: client.id })}
                                />
                            </div>
                            <div className="form-group"><label>Objet</label><input type="text" value={newSuivi.objet_inter} onChange={(e) => setNewSuivi({ ...newSuivi, objet_inter: e.target.value })} /></div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>N° Devis</label>
                                    <div className="devis-input-group">
                                        <input
                                            type="text"
                                            value={newSuivi.num_devis}
                                            onChange={(e) => setNewSuivi({ ...newSuivi, num_devis: e.target.value })}
                                        />
                                        <button
                                            type="button"
                                            className="btn-devis-search"
                                            title="Rechercher un devis dans Henrri"
                                            onClick={() => { setDevisSearchTerm(""); setShowDevisSearch("new"); }}
                                        >🔍</button>
                                        <button
                                            type="button"
                                            className="btn-devis-search"
                                            title="Voir le détail de ce devis"
                                            disabled={!newSuivi.num_devis}
                                            onClick={() => { setDevisSearchTerm(newSuivi.num_devis); setShowDevisSearch("new"); }}
                                        >👁</button>
                                    </div>
                                </div>
                                <div className="form-group"><label>Montant HT (€)</label><input type="number" step="0.01" value={newSuivi.montant_chantier} onChange={(e) => setNewSuivi({ ...newSuivi, montant_chantier: e.target.value })} /></div>
                            </div>
                            <div className="form-group">
                                <label>Responsable</label>
                                <select value={newSuivi.id_responsable} onChange={(e) => setNewSuivi({ ...newSuivi, id_responsable: e.target.value })}>
                                    {responsables.map(res => <option key={res.id} value={res.id}>{res.nom}</option>)}
                                </select>
                            </div>
                            <div className="form-group"><label>Échéance</label><input type="date" required value={newSuivi.date_echeance} onChange={(e) => setNewSuivi({ ...newSuivi, date_echeance: e.target.value })} /></div>
                            <div className="form-group">
                                <label>Notes / Commentaires</label>
                                <textarea placeholder="Infos supplémentaires..." value={newSuivi.notes} onChange={(e) => setNewSuivi({ ...newSuivi, notes: e.target.value })} />
                            </div>
                            <label className="custom-checkbox">
                                <input type="checkbox" checked={newSuivi.is_urgent === 1} onChange={(e) => setNewSuivi({ ...newSuivi, is_urgent: e.target.checked ? 1 : 0 })} />
                                <span className="checkmark"></span> Urgent
                            </label>
                            <div className="modal-btns">
                                <button type="submit" className="btn-add">Créer Dossier</button>
                                <button type="button" onClick={() => handleCloseModal("new", setShowModal)}>Annuler</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODALE ÉDITION */}
            {showEditModal && editingItem && (
                <div className={`modal-overlay ${closingModal === "edit" ? "closing" : ""}`}>
                    <div className="modal-content edit-suivi-modal">
                        <div className="edit-modal-heading">
                            <div className="edit-modal-title">
                                <span>Fiche dossier</span>
                                <h2>Modifier le dossier</h2>
                                <p>{editingItem.client_nom || "Dossier sans client"}</p>
                            </div>
                            <div className="edit-modal-heading-actions">
                                <button
                                    type="button"
                                    className={`btn-urgent-modal ${Number(editingItem.is_urgent) === 1 ? "active" : ""}`}
                                    aria-pressed={Number(editingItem.is_urgent) === 1}
                                    onClick={() => setEditingItem({
                                        ...editingItem,
                                        is_urgent: Number(editingItem.is_urgent) === 1 ? 0 : 1,
                                    })}
                                >
                                    <span aria-hidden="true">⚡</span>
                                    {Number(editingItem.is_urgent) === 1 ? "Urgent activé" : "Marquer urgent"}
                                </button>
                                <button
                                    type="button"
                                    className="edit-modal-close"
                                    onClick={() => handleCloseModal("edit", setShowEditModal)}
                                    aria-label="Fermer la fenêtre"
                                    title="Fermer"
                                >×</button>
                            </div>
                        </div>
                        <form className="edit-suivi-form" onSubmit={handleUpdateSuivi}>
                            <div className="edit-form-grid">
                            <div className="form-group">
                                <label>Catégorie</label>
                                <select value={editingItem.categorie || ""} onChange={(e) => setEditingItem({ ...editingItem, categorie: e.target.value })}>
                                    <option value="">-- Sans catégorie --</option>
                                    {CATEGORIES.filter(({ value }) => value).map(({ value, label }) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Client</label>
                                <ClientAutocomplete
                                    value={editingItem.client_nom}
                                    onChange={(nom) => setEditingItem({ ...editingItem, client_nom: nom })}
                                    onSelect={(client) => setEditingItem({ ...editingItem, client_nom: client.nom, henrri_customer_id: client.id })}
                                />
                            </div>
                            <div className="form-group edit-field-wide"><label>Objet de l'intervention</label><input type="text" value={editingItem.objet_inter || ""} onChange={(e) => setEditingItem({ ...editingItem, objet_inter: e.target.value })} /></div>
                            <div className="form-group">
                                    <label>Devis</label>
                                    <div className="devis-input-group">
                                        <input
                                            type="text"
                                            value={editingItem.num_devis || ""}
                                            onChange={(e) => setEditingItem({ ...editingItem, num_devis: e.target.value })}
                                        />
                                        <button
                                            type="button"
                                            className="btn-devis-search"
                                            title="Rechercher un devis dans Henrri"
                                            onClick={() => { setDevisSearchTerm(""); setShowDevisSearch("edit"); }}
                                        >🔍</button>
                                        <button
                                            type="button"
                                            className="btn-devis-search"
                                            title="Voir le détail de ce devis"
                                            disabled={!editingItem.num_devis}
                                            onClick={() => { setDevisSearchTerm(editingItem.num_devis || ""); setShowDevisSearch("edit"); }}
                                        >👁</button>
                                    </div>
                            </div>
                            <div className="form-group"><label>Montant du chantier</label><div className="edit-amount-field"><input type="number" value={editingItem.montant_chantier || ""} onChange={(e) => setEditingItem({ ...editingItem, montant_chantier: e.target.value })} /><span>€</span></div></div>
                            <div className="form-group edit-field-wide">
                                <label>Notes internes</label>
                                <textarea value={editingItem.notes || ""} onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })} placeholder="Ajouter une note..." />
                            </div>
                            </div>
                            <div className="edit-modal-footer">
                                <button className="edit-action edit-action-danger" type="button" onClick={() => handleDeleteSuivi(editingItem.id)}>
                                    <span aria-hidden="true">🗑</span>
                                    Supprimer
                                </button>
                                <div className="edit-modal-main-actions">
                                <button
                                    type="button"
                                    className="edit-action edit-action-workboard"
                                    disabled={workboardSendingId !== null}
                                    onClick={(event) => sendToWorkboard(event, editingItem)}
                                ><span aria-hidden="true">▣</span>{workboardSendingId === String(editingItem.id) ? "Envoi…" : "Workboard"}</button>
                                <button className="edit-action edit-action-secondary" type="button" onClick={() => handleCloseModal("edit", setShowEditModal)}>Annuler</button>
                                <button type="submit" className="edit-action edit-action-primary"><span aria-hidden="true">✓</span>Enregistrer</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODALE ANNÉE PRO */}
            {showAnneeProModal && (
                <div className={`modal-overlay ${closingModal === "anneepro" ? "closing" : ""}`}>
                    <div className="modal-content">
                        <h2>📅 Dossiers reportés — Année {nextYear} ({totalAnneePro.toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €)</h2>
                        {loadingAnneePro ? (
                            <p>Chargement...</p>
                        ) : anneeProItems.length === 0 ? (
                            <p>Aucun dossier reporté pour l'instant.</p>
                        ) : (
                            <div className="anneepro-list">
                                {anneeProItems.map(item => (
                                    <div key={item.id} className="anneepro-item">
                                        <div>
                                            <strong>{item.client_nom}</strong>
                                            <p className="objet-text">🛠 {item.objet_inter || "Pas d'objet"}</p>
                                            <span className="price-tag">{parseFloat(item.montant_chantier || 0).toFixed(2)}€</span>
                                        </div>
                                        <button
                                            className="btn-action2"
                                            onClick={() => handleRemettreMaintenant(item.id)}
                                            title="Remettre à planifier maintenant"
                                        >↩️ Remettre</button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="modal-btns">
                            <button type="button" onClick={() => handleCloseModal("anneepro", setShowAnneeProModal)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* POPUP RECHERCHE DEVIS HENRRI */}
            {globalDocument && <HenrriDevisModal key={globalDocument.nonce} initialDocumentId={globalDocument.documentId} documentType={globalDocument.documentType} onClose={() => setGlobalDocument(null)} />}
            {showDevisSearch && (
                <HenrriDevisModal
                    onClose={() => setShowDevisSearch(null)}
                    onSelect={handleSelectDevis}
                    search={devisSearchTerm}
                />
            )}

            {/* MODALE DEVIS VALIDÉS À TRAITER */}
            {showDevisValides && (
                <DevisValidesModal
                    onClose={() => setShowDevisValides(false)}
                    onImport={handleImportDevisValide}
                    onCountChange={setDevisValidesCount}
                />
            )}
        </div>
    );
};

export default SuiviProduction;
